import { useState, type ReactNode } from 'react';
import { Plus } from 'lucide-react';
import { apiErrorMessage } from '@/api/baseApi';
import { Alert, Button, Select } from '@/components/ui';

export interface SelectOption {
  id: string;
  label: string;
}

interface CreatableSelectProps {
  label: string;
  value: string;
  onChange: (id: string) => void;
  options: SelectOption[];
  /** Singular, lower case — drives the "Add a …" wording. */
  noun: string;
  /** The fields of the inline form. The caller owns their state. */
  form: ReactNode;
  /** Performs the create. Returns the new id, which is then selected. */
  onCreate: () => Promise<string>;
  onReset: () => void;
  createDisabled?: boolean;
  creating?: boolean;
  /** Whether the member may create one at all. */
  canCreate?: boolean;
  placeholder?: string;
  hint?: string;
  error?: string;
  required?: boolean;
}

/**
 * A dropdown that can create the record it is missing.
 *
 * Reference data is per-tenant, so a business will meet a missing category,
 * brand or unit half-way through a form. Sending them to another screen to add
 * it loses whatever they had already typed; creating it here keeps the flow
 * intact, and the new record is persisted so every other dropdown picks it up.
 */
export function CreatableSelect({
  label,
  value,
  onChange,
  options,
  noun,
  form,
  onCreate,
  onReset,
  createDisabled,
  creating,
  canCreate = true,
  placeholder,
  hint,
  error,
  required,
}: CreatableSelectProps) {
  const [adding, setAdding] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const close = (): void => {
    setAdding(false);
    setFailure(null);
    onReset();
  };

  const submit = async (): Promise<void> => {
    setFailure(null);
    try {
      // Selecting it is the whole point of creating it here.
      onChange(await onCreate());
      close();
    } catch (creationError) {
      // A duplicate name comes back as a 409 with a message worth showing, and
      // the form stays open so what they typed can be corrected.
      setFailure(apiErrorMessage(creationError, `That ${noun} could not be created`));
    }
  };

  return (
    <div>
      <Select
        label={label}
        required={required}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        hint={hint}
        error={error}
      >
        <option value="">{placeholder ?? `Select a ${noun}`}</option>
        {options.map((option) => (
          <option key={option.id} value={option.id}>{option.label}</option>
        ))}
      </Select>

      {canCreate && !adding && (
        <button
          type="button"
          className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-brand-600 hover:underline dark:text-brand-300"
          onClick={() => {
            setFailure(null);
            setAdding(true);
          }}
        >
          <Plus className="size-3.5" aria-hidden />
          {options.length === 0 ? `Create the first ${noun}` : `Not listed? Add a ${noun}`}
        </button>
      )}

      {adding && (
        <div className="mt-3 space-y-3 rounded-lg border border-subtle bg-sunken p-3">
          <p className="text-xs text-muted">
            Saved to the business and available everywhere, not just here.
          </p>

          {failure && <Alert tone="danger">{failure}</Alert>}

          {form}

          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={close}>Cancel</Button>
            <Button
              size="sm"
              variant="outline"
              loading={creating}
              disabled={createDisabled}
              onClick={() => void submit()}
            >
              Create and select
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
